/*
 * combined-variant-select.js — TICK-3690
 *
 * Behavior for <combined-variant-select> (snippets/product-variant-options-combined.liquid):
 * two stacked dropdowns listing FULL variant combinations, grouped by resolved
 * stock state (in_stock → "Ready to Ship", mto/sold_out → "Made to Order").
 *
 * Selecting a row writes the per-option hidden inputs and dispatches ONE
 * bubbling 'change' — the theme's ProductFormReader derives the variant from
 * the inputs and fires theme:variant:change, which drives price/image/ATC,
 * history state and variant-availability.js. We never touch input[name="id"].
 *
 * Swatch chips resolve by option name + value from three sources, in
 * priority order (TICK-3738): Shopify's native option-value swatches
 * ([data-native-swatch-map]), the hidden Swatch King markup on the page
 * (harvestSwatchKing — real client material photos), then the theme's
 * swatches.json (same source + normalization as theme.js ColorMatch). Rows
 * with no match just render text — that is the designed fallback.
 */
(function () {
  'use strict';

  if (customElements.get('combined-variant-select')) return;

  // theme.css shows .select-popout__list via this class (PopoutSelect parity)
  var LIST_VISIBLE = 'popout-list--visible';
  var IMAGE_RE = /\.(jpg|jpeg|png|svg|webp|avif|gif)(\?|$)/i;
  var URL_IN_STYLE_RE = /url\(\s*["']?([^"')]+)["']?\s*\)/;
  var SWAP_MS = 180;
  var MOVE_MS = 200;

  function normalizeKey(k) {
    return String(k).toLowerCase().replace(/\s/g, '');
  }

  class CombinedVariantSelect extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-section-type="product"]') || document;

      // Per-variant stock map rendered by product-variant-stock-data.liquid
      this.map = {};
      var mapEl = this.section.querySelector('[data-variant-stock-map]');
      if (mapEl) {
        try {
          this.map = JSON.parse(mapEl.textContent) || {};
        } catch (e) {
          this.map = {};
        }
      }

      this.swatchBase = (window.theme && window.theme.assets && window.theme.assets.base) || '/';
      this.swatchLookup = null; // { normalizedName: value }

      // Option names in position order — from the section's [data-stock-i18n]
      // payload (rendered on this template) when present, else derived from the
      // hidden inputs' name="options[...]" attributes in DOM order.
      this.optionNames = [];
      var i18nEl = this.section.querySelector('[data-stock-i18n]');
      if (i18nEl) {
        try {
          var i18n = JSON.parse(i18nEl.textContent) || {};
          if (Array.isArray(i18n.optionNames)) this.optionNames = i18n.optionNames.slice();
        } catch (e) {
          this.optionNames = [];
        }
      }
      if (!this.optionNames.length) {
        Array.prototype.forEach.call(this.querySelectorAll('[data-single-option-selector]'), function (input) {
          var match = /^options\[(.+)\]$/.exec(input.getAttribute('name') || '');
          this.optionNames.push(match ? match[1] : '');
        }, this);
      }

      // Material slots: the first two options that are NOT size-like get the
      // chip halves, in option order — on Bystander that is Top Material
      // (slot 0, wood) then Base Upholstery (slot 1, leather); Height/size and
      // Orientation options never get a half. The keyword lists are mirrored
      // in classifyOption() in variant-availability.js — keep in sync.
      this.materialPositions = [];
      this.optionNames.forEach(function (name, index) {
        if (this.materialPositions.length >= 2) return;
        // TICK-3927: 'style' (Upholstery Style) is not a material either.
        if (/(size|height|dimension|orientation|chaise|facing|style)/i.test(String(name))) return;
        this.materialPositions.push(index);
      }, this);

      // Native option-value swatch map ([data-native-swatch-map], keyed
      // "<option name>||<value name>") — highest-priority chip source.
      this.nativeLookup = {};
      var nativeEl = this.section.querySelector('[data-native-swatch-map]');
      if (nativeEl) {
        try {
          var native = JSON.parse(nativeEl.textContent) || {};
          Object.keys(native).forEach(function (key) {
            var parts = key.split('||');
            this.nativeLookup[normalizeKey(parts[0]) + '||' + normalizeKey(parts[1] || '')] = native[key];
          }, this);
        } catch (e) {
          this.nativeLookup = {};
        }
      }

      // Theme-supplied override map ([data-combined-swatch-overrides], keyed
      // "<option name>||<value name>") — checked after native swatches, before
      // the Swatch King harvest. Exists because Swatch King renders nothing on
      // this combined-dropdown template (its mount stays empty), so the
      // harvest can never see materials that appear on no other template.
      this.overrideLookup = {};
      var overridesEl = this.section.querySelector('[data-combined-swatch-overrides]');
      if (overridesEl) {
        try {
          var overrides = JSON.parse(overridesEl.textContent) || {};
          Object.keys(overrides).forEach(function (key) {
            var parts = key.split('||');
            this.overrideLookup[normalizeKey(parts[0]) + '||' + normalizeKey(parts[1] || '')] = overrides[key];
          }, this);
        } catch (e) {
          this.overrideLookup = {};
        }
      }

      // Bind once — connectedCallback re-runs when the theme editor re-parents
      // the element, and re-bound handlers would stack duplicate listeners.
      if (!this.handlersBound) {
        this.handlersBound = true;
        this.onClick = this.onClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onVariantsHydrated = this.onVariantsHydrated.bind(this);
      }

      this.addEventListener('click', this.onClick);
      this.addEventListener('keyup', this.onKeyUp);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('theme:variant:change', this.onVariantChange);
      document.addEventListener('theme:product:variants-hydrated', this.onVariantsHydrated);

      var self = this;
      this.loadSwatches().then(function () {
        self.harvestSwatchKing();
        self.paintRowChips();
        self.updateToggles(self.selectedVariantId(), false);
      });

      // Swatch King injects/re-renders its (hidden) markup async — re-harvest
      // on section mutations, debounced. Chip painting only touches attributes
      // and inline styles inside this element, so a childList observer cannot
      // loop on its own repaints; the changed-guard keeps repaints down anyway.
      this.skDebounce = null;
      this.skObserver = new MutationObserver(function () {
        if (self.skDebounce) clearTimeout(self.skDebounce);
        self.skDebounce = setTimeout(function () {
          self.skDebounce = null;
          if (self.harvestSwatchKing()) {
            self.paintRowChips();
            self.updateToggles(self.selectedVariantId(), false);
          }
        }, 150);
      });
      this.skObserver.observe(this.section, { childList: true, subtree: true });

      this.regroup();
    }

    disconnectedCallback() {
      if (this.skObserver) {
        this.skObserver.disconnect();
        this.skObserver = null;
      }
      if (this.skDebounce) {
        clearTimeout(this.skDebounce);
        this.skDebounce = null;
      }
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keyup', this.onKeyUp);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('theme:variant:change', this.onVariantChange);
      document.removeEventListener('theme:product:variants-hydrated', this.onVariantsHydrated);
    }

    // ── Swatch resolution ────────────────────────────────────────────────────

    // window.theme.assets.swatches is the swatches.json asset URL in this
    // theme (see layout/theme.liquid); fetch it once — same-origin, cached.
    // Also accept a pre-parsed array/object so we never fetch when the data
    // is already available on the page.
    loadSwatches() {
      var self = this;
      if (this.swatchLookup) return Promise.resolve(this.swatchLookup);

      var src = window.theme && window.theme.assets && window.theme.assets.swatches;
      var entriesPromise;

      if (Array.isArray(src)) {
        entriesPromise = Promise.resolve(src);
      } else if (src && Array.isArray(src.colors)) {
        entriesPromise = Promise.resolve(src.colors);
      } else if (typeof src === 'string' && src !== '') {
        entriesPromise = window
          .fetch(src)
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            return (data && data.colors) || [];
          })
          .catch(function () {
            return [];
          });
      } else {
        entriesPromise = Promise.resolve([]);
      }

      return entriesPromise.then(function (entries) {
        var lookup = {};
        entries.forEach(function (entry) {
          if (!entry || typeof entry !== 'object') return;
          Object.keys(entry).forEach(function (name) {
            var key = normalizeKey(name);
            if (!(key in lookup)) lookup[key] = entry[name];
          });
        });
        self.swatchLookup = lookup;
        return lookup;
      });
    }

    // Swatch King renders its own (CSS-hidden) markup on this template with
    // the client's real material images — harvest them into an "opt||value"
    // lookup (plus "||value" when the group's option name can't be read).
    // Selectors mirror optionNameOf()/swatchValue() in variant-availability.js.
    // Returns true when the lookup gained or changed any entry.
    harvestSwatchKing() {
      var self = this;
      if (!this.skLookup) this.skLookup = {};
      var changed = false;

      // Swatch King paints its chips from stylesheet rules, not inline styles,
      // so an inline-only read finds nothing and the whole harvest comes back
      // empty (TICK-3738 QA). Check the inline attribute first — it is cheap
      // and wins when present — then fall back to the computed value.
      // background-image still computes on display:none nodes, so hidden
      // groups resolve fine. Gradients yield no url() match and are skipped.
      function urlFromStyle(el) {
        if (!el) return '';
        var inline = el.getAttribute('style') || '';
        var match = inline.indexOf('background-image') === -1 ? null : URL_IN_STYLE_RE.exec(inline);
        if (match) return match[1];
        var computed = '';
        try {
          computed = window.getComputedStyle(el).backgroundImage || '';
        } catch (e) {
          return '';
        }
        if (!computed || computed === 'none') return '';
        match = URL_IN_STYLE_RE.exec(computed);
        return match ? match[1] : '';
      }

      function imageOf(sw) {
        var img = sw.tagName === 'IMG' ? sw : sw.querySelector('img');
        if (img) {
          // Groups are display:none, so lazyloaders may never fire — data-src too.
          var src = img.currentSrc || img.src || img.getAttribute('data-src');
          if (src) return src;
        }
        var url = urlFromStyle(sw);
        if (url) return url;
        // Named hosts first (cheap), then any descendant — Swatch King's preset
        // markup varies by view type and the image often sits on an inner node
        // with no class we can predict.
        var hosts = ['.star-set-image', '.swatch-custom-image', '.swatch-image-wrapper'];
        for (var i = 0; i < hosts.length && !url; i++) {
          url = urlFromStyle(sw.querySelector(hosts[i]));
        }
        if (url) return url;
        var kids = sw.querySelectorAll('*');
        for (var j = 0; j < kids.length && !url; j++) {
          url = urlFromStyle(kids[j]);
        }
        return url;
      }

      function valueOf(sw) {
        var titleEl = sw.querySelector('.swatch-button-title-text');
        if (titleEl) return titleEl.textContent.trim();
        // Own text, excluding the stock label variant-availability.js injects
        // (it tags these hidden swatches too, so the label WILL be present).
        var raw = '';
        sw.childNodes.forEach(function (node) {
          if (node.nodeType === 1 && node.classList.contains('sk-stock-label')) return;
          raw += node.textContent || '';
        });
        return raw.trim();
      }

      function store(key, url) {
        var image = 'url("' + url + '")';
        var entry = self.skLookup[key];
        if (entry && entry.image === image) return;
        self.skLookup[key] = { image: image };
        changed = true;
      }

      Array.prototype.forEach.call(this.section.querySelectorAll('.swatch-single'), function (group) {
        var nameEl = group.querySelector('.swatch-option-name')
          || group.querySelector('.swatch-label-container')
          || group.querySelector('.swatch-label');
        var optionName = nameEl ? String(nameEl.textContent).split(':')[0].trim() : '';

        Array.prototype.forEach.call(group.querySelectorAll('.swatch-selector'), function (sw) {
          var value = valueOf(sw);
          if (!value) return;
          var url = imageOf(sw);
          if (!url) return; // no image — skip, lower-priority sources still apply
          if (optionName) {
            store(normalizeKey(optionName) + '||' + normalizeKey(value), url);
          } else {
            store('||' + normalizeKey(value), url);
          }
        });
      });

      return changed;
    }

    // String entries (native map / swatches.json): absolute image URLs pass
    // through untouched; bare filenames resolve against the theme asset base;
    // anything non-image is a color.
    stringSwatch(value) {
      if (IMAGE_RE.test(value)) {
        if (/^(https?:)?\/\//i.test(value)) {
          return { image: 'url("' + value + '")' };
        }
        return { image: 'url(' + this.swatchBase + encodeURIComponent(value) + ')' };
      }
      return { color: value };
    }

    // Returns { color } or { image } or null for an option name + value,
    // checking native admin swatches, the theme override map, the Swatch King
    // harvest, then swatches.json.
    resolveSwatch(optionName, value) {
      var valKey = normalizeKey(value);
      var pairKey = normalizeKey(optionName || '') + '||' + valKey;

      var native = this.nativeLookup ? this.nativeLookup[pairKey] : null;
      if (typeof native === 'string' && native !== '') return this.stringSwatch(native);

      if (this.overrideLookup) {
        var override = this.overrideLookup[pairKey] || this.overrideLookup['||' + valKey];
        if (typeof override === 'string' && override !== '') return this.stringSwatch(override);
      }

      if (this.skLookup) {
        var harvested = this.skLookup[pairKey] || this.skLookup['||' + valKey];
        if (harvested) return harvested; // already { image: ... }
      }

      if (!this.swatchLookup) return null;
      var entry = this.swatchLookup[valKey];
      if (typeof entry !== 'string' || entry === '') return null;
      return this.stringSwatch(entry);
    }

    styleHalf(half, resolved) {
      if (!half) return;
      if (!resolved) {
        // Clear a previously painted half whose slot is now null.
        half.style.backgroundImage = '';
        half.style.backgroundColor = '';
        return;
      }
      if (resolved.image) {
        half.style.backgroundImage = resolved.image;
        half.style.backgroundColor = '';
      } else {
        half.style.backgroundColor = resolved.color;
        half.style.backgroundImage = '';
      }
    }

    // matches: fixed slot array (leather, wood) — entries may be null
    applyChip(chip, matches) {
      if (!chip) return;
      var halves = chip.querySelectorAll('.combined-swatch__half');
      var resolved = matches.filter(function (entry) {
        return entry;
      });
      if (!resolved.length) {
        chip.hidden = true;
        return;
      }
      if (resolved.length === 1) {
        chip.classList.add('combined-swatch--single');
        this.styleHalf(halves[0], resolved[0]);
        this.styleHalf(halves[1], null);
      } else {
        chip.classList.remove('combined-swatch--single');
        this.styleHalf(halves[0], matches[0]);
        this.styleHalf(halves[1], matches[1]);
      }
      chip.hidden = false;
    }

    // Fixed slot array [leatherOrNull, woodOrNull] driven by materialPositions
    // so a failed leather never shifts the wood into half 1. Falls back to the
    // legacy first-two-resolving behavior when option names are unreadable.
    rowMatches(row) {
      var values = this.rowOptions(row);
      if (this.materialPositions && this.materialPositions.length) {
        return this.materialPositions.map(function (pos) {
          var value = values[pos];
          if (value == null || value === '') return null;
          return this.resolveSwatch(this.optionNames[pos], value) || null;
        }, this);
      }
      var matches = [];
      values.forEach(function (value) {
        if (matches.length >= 2) return;
        var resolved = this.resolveSwatch('', value);
        if (resolved) matches.push(resolved);
      }, this);
      return matches;
    }

    paintRowChips() {
      this.queryRows().forEach(function (row) {
        this.applyChip(row.querySelector('[data-combined-swatch]'), this.rowMatches(row));
      }, this);
    }

    // ── Helpers ─────────────────────────────────────────────────────────────

    queryRows() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-combined-option]'));
    }

    rowOptions(row) {
      try {
        return JSON.parse(row.getAttribute('data-option-values')) || [];
      } catch (e) {
        return [];
      }
    }

    rowById(id) {
      if (!id) return null;
      return this.querySelector('[data-combined-option][data-variant-id="' + id + '"]');
    }

    selectedVariantId() {
      var row = this.querySelector('[data-combined-option][aria-selected="true"]');
      return row ? row.getAttribute('data-variant-id') : null;
    }

    listFor(toggle) {
      var popout = toggle.closest('.select-popout');
      return popout ? popout.querySelector('[data-combined-list]') : null;
    }

    // ── Open / close ────────────────────────────────────────────────────────

    openToggle(toggle) {
      this.closeAll(toggle);
      var list = this.listFor(toggle);
      toggle.setAttribute('aria-expanded', 'true');
      if (list) list.classList.add(LIST_VISIBLE);
    }

    closeToggle(toggle) {
      var list = this.listFor(toggle);
      toggle.setAttribute('aria-expanded', 'false');
      if (list) list.classList.remove(LIST_VISIBLE);
    }

    closeAll(except) {
      Array.prototype.forEach.call(this.querySelectorAll('[data-combined-toggle]'), function (toggle) {
        if (toggle !== except) this.closeToggle(toggle);
      }, this);
    }

    onClick(event) {
      var toggle = event.target.closest('[data-combined-toggle]');
      if (toggle && this.contains(toggle)) {
        if (toggle.getAttribute('aria-expanded') === 'true') {
          this.closeToggle(toggle);
        } else {
          this.openToggle(toggle);
        }
        return;
      }

      var row = event.target.closest('[data-combined-option]');
      if (row && this.contains(row)) {
        this.selectRow(row);
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.closeAll();
    }

    onFocusOut(event) {
      if (!event.relatedTarget || !this.contains(event.relatedTarget)) this.closeAll();
    }

    onKeyUp(event) {
      if (event.key !== 'Escape') return;
      var open = this.querySelector('[data-combined-toggle][aria-expanded="true"]');
      this.closeAll();
      if (open) open.focus();
    }

    // ── Selection → hidden inputs → one change event ────────────────────────

    selectRow(row) {
      var values = this.rowOptions(row);
      var lastInput = null;
      values.forEach(function (value, index) {
        var input = this.querySelector('[data-single-option-selector][data-index="option' + (index + 1) + '"]');
        if (!input) return;
        input.value = value;
        lastInput = input;
      }, this);
      this.closeAll();
      if (lastInput) lastInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // ── theme:variant:change → current markers + toggle swap ────────────────

    onVariantChange(event) {
      var variant = event.detail && event.detail.variant;
      if (!variant || !variant.id) return;
      var id = String(variant.id);
      if (!this.rowById(id)) return; // not this product

      this.queryRows().forEach(function (row) {
        var isCurrent = row.getAttribute('data-variant-id') === id;
        row.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
        var item = row.closest('.select-popout__item');
        if (item) item.classList.toggle('select-popout__item--current', isCurrent);
      });

      this.updateToggles(id, true);
    }

    setToggleContent(toggle, id) {
      var label = toggle.querySelector('[data-combined-toggle-label]');
      var chip = toggle.querySelector('[data-combined-toggle-swatch]');
      var group = toggle.closest('[data-combined-group]');
      var row = id ? this.rowById(id) : null;
      var selectedHere = row && group && group.contains(row);

      if (selectedHere) {
        if (label) {
          label.textContent = this.rowOptions(row).join(' / ');
          label.classList.remove('combined-select__toggle-label--placeholder');
        }
        this.applyChip(chip, this.rowMatches(row));
      } else {
        var empty = group ? group.querySelector('[data-combined-empty]') : null;
        var hasRows = group && group.querySelector('[data-combined-option]');
        if (label) {
          label.textContent = hasRows
            ? toggle.getAttribute('data-placeholder') || ''
            : (empty ? empty.textContent : '');
          label.classList.add('combined-select__toggle-label--placeholder');
        }
        this.applyChip(chip, []);
      }
    }

    updateToggles(id, animate) {
      var self = this;
      var toggles = Array.prototype.slice.call(this.querySelectorAll('[data-combined-toggle]'));
      if (!animate) {
        toggles.forEach(function (toggle) {
          self.setToggleContent(toggle, id);
        });
        return;
      }
      toggles.forEach(function (toggle) {
        toggle.classList.add('is-swapping');
      });
      setTimeout(function () {
        toggles.forEach(function (toggle) {
          self.setToggleContent(toggle, id);
          toggle.classList.remove('is-swapping');
        });
      }, SWAP_MS);
    }

    // ── Regrouping (stock map is the source of truth) ───────────────────────

    moveRow(row, targetKey) {
      var item = row.closest('.select-popout__item');
      var list = this.querySelector('[data-combined-group="' + targetKey + '"] [data-combined-list]');
      if (!item || !list) return;

      var index = parseInt(row.getAttribute('data-row-index'), 10) || 0;
      var before = null;
      Array.prototype.some.call(list.querySelectorAll('[data-combined-option]'), function (other) {
        if ((parseInt(other.getAttribute('data-row-index'), 10) || 0) > index) {
          before = other.closest('.select-popout__item');
          return true;
        }
        return false;
      });
      if (!before) before = list.querySelector('[data-combined-empty]');

      if (before) {
        list.insertBefore(item, before);
      } else {
        list.appendChild(item);
      }
    }

    updateGroupStates() {
      Array.prototype.forEach.call(this.querySelectorAll('[data-combined-group]'), function (group) {
        var hasRows = !!group.querySelector('[data-combined-option]');
        var empty = group.querySelector('[data-combined-empty]');
        var toggle = group.querySelector('[data-combined-toggle]');
        if (empty) empty.hidden = hasRows;
        if (toggle) {
          toggle.disabled = !hasRows;
          toggle.classList.toggle('combined-select__toggle--empty', !hasRows);
          if (!hasRows) this.closeToggle(toggle);
        }
      }, this);
    }

    regroup() {
      var self = this;
      // Guard: an empty/unparsable map must not dump every row into MTO.
      if (!this.map || !Object.keys(this.map).length) return;

      var moves = [];
      var selectedMoved = false;
      this.queryRows().forEach(function (row) {
        var entry = self.map[row.getAttribute('data-variant-id')];
        var target = entry && entry.s === 'in_stock' ? 'in_stock' : 'mto';
        var group = row.closest('[data-combined-group]');
        if (!group || group.getAttribute('data-combined-group') === target) return;
        moves.push({ row: row, target: target });
        if (row.getAttribute('aria-selected') === 'true') selectedMoved = true;
      });

      if (!moves.length) {
        this.updateGroupStates();
        return;
      }

      moves.forEach(function (move) {
        move.row.classList.add('is-moving');
      });
      setTimeout(function () {
        moves.forEach(function (move) {
          self.moveRow(move.row, move.target);
        });
        requestAnimationFrame(function () {
          moves.forEach(function (move) {
            move.row.classList.remove('is-moving');
          });
        });
        self.updateGroupStates();
        // The form inputs never change here; only the toggle presentation
        // follows the selected row into its new group.
        if (selectedMoved) self.updateToggles(self.selectedVariantId(), true);
      }, MOVE_MS);
    }

    onVariantsHydrated(event) {
      var extra = event.detail && event.detail.map;
      if (!extra) return;
      var self = this;
      Object.keys(extra).forEach(function (id) {
        if (!self.map[id]) self.map[id] = extra[id];
      });
      this.regroup();
    }
  }

  customElements.define('combined-variant-select', CombinedVariantSelect);
})();
